/**
 * The SVG chart — docs/08-frontend-and-ux.md §4.
 *
 * One scene `<g>` carries the pan/zoom transform, so panning is a single
 * attribute write per frame and never re-renders React (§4.8). Everything inside
 * it is laid out in logical coordinates by the pure engine in `layout/`, which
 * is why this file contains no geometry decisions at all — only how the geometry
 * is drawn.
 *
 * Two accessibility commitments shape the markup (§10):
 *
 *  - **Every card is focusable and reachable by keyboard.** A chart you can only
 *    explore by dragging is a chart half the family cannot use. Arrow keys walk
 *    the graph in the direction the reader means — up to parents, down to
 *    children, sideways along the band.
 *  - **Nothing is encoded by colour alone.** Adoption, fostering and guardianship
 *    each carry their own dash pattern, and every edge carries a `<title>`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatFuzzyDate, formatLifespan, type PersonSummary } from '@famtree/shared';
import type { SceneSubset } from './views.js';
import { layoutTree, DEFAULT_LAYOUT, type TreeLayout } from './layout/index.js';
import styles from './tree.module.css';

export interface TreeChartProps {
  scene: SceneSubset;
  /** `[+N]` chips: how many people lie beyond each boundary person. */
  hidden: Record<string, { ancestors: number; descendants: number }>;
  onPivot: (personId: string) => void;
  onOpen: (personId: string) => void;
  /**
   * The person under the pointer or the focus ring.
   *
   * The chart does not ask *what* they are to the reader — that is a network
   * call, and a component that draws two hundred cards has no business making
   * one. It reports who is being looked at and lets the page answer.
   */
  onInspect?: (personId: string | null) => void;
}

/** Zoom below which cards lose their dates, and then their text entirely (§4.8). */
const LOD_MID = 0.4;
const LOD_LOW = 0.15;

export function TreeChart({
  scene,
  hidden,
  onPivot,
  onOpen,
  onInspect,
}: TreeChartProps): JSX.Element {
  const { t } = useTranslation();
  const frameRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<SVGGElement>(null);
  const [camera, setCamera] = useState({ x: 0, y: 0, scale: 1 });
  const [selected, setSelected] = useState<string | null>(scene.focus);

  const layout = useMemo(() => layoutTree(scene), [scene]);

  // Fit the whole scene on first sight of it, so a chart never opens showing the
  // top-left corner of a family.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || layout.width === 0) return;
    const { width, height } = frame.getBoundingClientRect();
    const scale = Math.min(width / (layout.width + 80), height / (layout.height + 80), 1);
    setCamera({
      x: (width - layout.width * scale) / 2,
      y: 24,
      scale: Math.max(scale, LOD_LOW),
    });
    setSelected(scene.focus);
  }, [layout.width, layout.height, scene.focus]);

  /* ---------------------------------------------------------------- panning */

  const drag = useRef<{ x: number; y: number } | null>(null);
  const onPointerDown = (event: React.PointerEvent): void => {
    drag.current = { x: event.clientX - camera.x, y: event.clientY - camera.y };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const onPointerMove = (event: React.PointerEvent): void => {
    if (!drag.current) return;
    const next = { x: event.clientX - drag.current.x, y: event.clientY - drag.current.y };
    // Written straight to the DOM: a pan that re-rendered React would drop
    // frames on a large chart for no benefit (§4.8).
    sceneRef.current?.setAttribute(
      'transform',
      `translate(${String(next.x)},${String(next.y)}) scale(${String(camera.scale)})`,
    );
    drag.current = { ...drag.current, ...{} };
    pending.current = next;
  };
  const pending = useRef<{ x: number; y: number } | null>(null);
  const onPointerUp = (): void => {
    drag.current = null;
    if (pending.current) {
      setCamera((current) => ({ ...current, ...pending.current! }));
      pending.current = null;
    }
  };

  const zoomBy = useCallback((factor: number): void => {
    setCamera((current) => ({
      ...current,
      scale: Math.min(3, Math.max(0.08, current.scale * factor)),
    }));
  }, []);

  /* ------------------------------------------------- keyboard navigation */

  const neighbours = useMemo(() => {
    const parents = new Map<string, string[]>();
    const children = new Map<string, string[]>();
    for (const drop of layout.drops) {
      parents.set(drop.childId, [...(parents.get(drop.childId) ?? []), drop.parentId]);
      children.set(drop.parentId, [...(children.get(drop.parentId) ?? []), drop.childId]);
    }
    const band = new Map<number, string[]>();
    for (const placed of [...layout.persons.values()].sort((a, b) => a.x - b.x)) {
      band.set(placed.generation, [...(band.get(placed.generation) ?? []), placed.id]);
    }
    return { parents, children, band };
  }, [layout]);

  const move = useCallback(
    (direction: 'up' | 'down' | 'left' | 'right'): void => {
      if (!selected) return;
      const here = layout.persons.get(selected);
      if (!here) return;

      if (direction === 'up' || direction === 'down') {
        const list =
          direction === 'up'
            ? (neighbours.parents.get(selected) ?? [])
            : (neighbours.children.get(selected) ?? []);
        // Nearest in reading order, so the eye and the focus ring agree.
        const nearest = [...list].sort(
          (a, b) =>
            Math.abs((layout.persons.get(a)?.x ?? 0) - here.x) -
            Math.abs((layout.persons.get(b)?.x ?? 0) - here.x),
        )[0];
        if (nearest) setSelected(nearest);
        return;
      }

      const row = neighbours.band.get(here.generation) ?? [];
      const index = row.indexOf(selected);
      const next = row[index + (direction === 'right' ? 1 : -1)];
      if (next) setSelected(next);
    },
    [layout, neighbours, selected],
  );

  useEffect(() => {
    if (!selected) return;
    const node = document.getElementById(`tree-card-${selected}`);
    node?.focus({ preventScroll: true });
    // Keyboard users get the kinship read-out too: it follows the focus ring,
    // not the pointer, or half the family never sees it (§10).
    onInspect?.(selected);
  }, [selected, onInspect]);

  const onKeyDown = (event: React.KeyboardEvent): void => {
    const keys: Record<string, 'up' | 'down' | 'left' | 'right'> = {
      ArrowUp: 'up',
      ArrowDown: 'down',
      ArrowLeft: 'left',
      ArrowRight: 'right',
    };
    const direction = keys[event.key];
    if (direction) {
      event.preventDefault();
      move(direction);
      return;
    }
    if (event.key === 'Enter' && selected) {
      event.preventDefault();
      onOpen(selected);
    }
    if (event.key === '+' || event.key === '=') zoomBy(1.2);
    if (event.key === '-') zoomBy(1 / 1.2);
  };

  const lod = camera.scale >= LOD_MID ? 'full' : camera.scale >= LOD_LOW ? 'mid' : 'low';

  return (
    <div
      className={styles.frame}
      ref={frameRef}
      onKeyDown={onKeyDown}
      // The chart is one composite widget; the cards inside it are the stops.
      role="application"
      aria-label={t('tree.chartLabel')}
    >
      <svg
        className={styles.canvas}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={(event) => zoomBy(event.deltaY < 0 ? 1.08 : 1 / 1.08)}
      >
        <g
          ref={sceneRef}
          transform={`translate(${String(camera.x)},${String(camera.y)}) scale(${String(camera.scale)})`}
        >
          <Bands
            layout={layout}
            focusGeneration={layout.persons.get(scene.focus)?.generation ?? 0}
          />
          <Drops layout={layout} />
          <Unions layout={layout} lod={lod} />
          <Brackets layout={layout} />
          <Cards
            layout={layout}
            focus={scene.focus}
            selected={selected}
            hidden={hidden}
            lod={lod}
            onSelect={setSelected}
            onPivot={onPivot}
            onOpen={onOpen}
            onInspect={onInspect}
          />
        </g>
      </svg>
    </div>
  );
}

/* ------------------------------------------------------------------- pieces */

function Bands({
  layout,
  focusGeneration,
}: {
  layout: TreeLayout;
  focusGeneration: number;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <g aria-hidden="true">
      {layout.bands.map((band) => {
        // §4.6: band labels are relative to the pivot, with the absolute offset
        // beside them so two readers can refer to the same band unambiguously.
        const offset = band.generation - focusGeneration;
        const key =
          offset === 0
            ? 'focus'
            : offset === -1
              ? 'parents'
              : offset === -2
                ? 'grandparents'
                : offset === 1
                  ? 'children'
                  : offset === 2
                    ? 'grandchildren'
                    : offset < 0
                      ? 'ancestors'
                      : 'descendants';
        return (
          <g key={band.generation}>
            <rect
              className={band.generation % 2 === 0 ? styles.bandA : styles.bandB}
              x={-4000}
              y={band.y - DEFAULT_LAYOUT.bandGap / 2}
              width={layout.width + 8000}
              height={band.height}
            />
            <text className={styles.bandLabel} x={-3980} y={band.y + 16}>
              {t(`tree.bands.${key}`)} ·{' '}
              {offset >= 0 ? `G+${String(offset)}` : `G${String(offset)}`}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function Unions({ layout, lod }: { layout: TreeLayout; lod: string }): JSX.Element {
  const { t } = useTranslation();
  return (
    <g>
      {layout.junctions.map((junction) => {
        const a = layout.persons.get(junction.a);
        const b = layout.persons.get(junction.b);
        if (!a || !b) return null;
        const y = Math.max(a.y, b.y) + DEFAULT_LAYOUT.cardHeight / 2;
        const [left, right] = a.x <= b.x ? [a, b] : [b, a];
        const faded = junction.endReason === 'separation' || junction.endReason === 'dissolved';
        const ended = junction.ended != null || junction.endReason != null;

        const chip = [
          junction.unionType === 'partnership' ? 'p.' : 'm.',
          formatFuzzyDate(junction.started) ?? '',
          ended ? `–${formatFuzzyDate(junction.ended) ?? '?'}` : '–',
        ].join('');

        return (
          <g key={junction.id}>
            <title>
              {t(`tree.unionState.${junction.endReason ?? (ended ? 'unknown' : 'ongoing')}`)}
            </title>
            <path
              className={faded ? styles.unionBarFaded : styles.unionBar}
              d={`M ${String(left.x + DEFAULT_LAYOUT.cardWidth)} ${String(y)} L ${String(right.x)} ${String(y)}`}
              // §4.4: an ended union is broken, not merely coloured differently.
              strokeDasharray={ended && !faded ? '10 4' : undefined}
            />
            <path
              className={styles.unionBar}
              d={`M ${String(junction.x)} ${String(y)} L ${String(junction.x)} ${String(junction.y)}`}
            />
            {lod === 'full' && (
              <text className={styles.unionChip} x={junction.x} y={y - 6} textAnchor="middle">
                {chip}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
}

function Drops({ layout }: { layout: TreeLayout }): JSX.Element {
  const { t } = useTranslation();
  const junctionById = new Map(layout.junctions.map((junction) => [junction.id, junction]));
  return (
    <g>
      {layout.drops.map((drop) => {
        const child = layout.persons.get(drop.childId);
        const parent = layout.persons.get(drop.parentId);
        if (!child || !parent) return null;

        const junction = drop.junctionId ? junctionById.get(drop.junctionId) : undefined;
        // A child of a recorded couple hangs from their junction; a child whose
        // parents were never a couple gets a line from each of them, which is an
        // honest picture of the two independent assertions (§4.1).
        const fromX = junction ? junction.x : parent.x + DEFAULT_LAYOUT.cardWidth / 2;
        const fromY = junction ? junction.y : parent.y + DEFAULT_LAYOUT.cardHeight;
        const toX = child.x + DEFAULT_LAYOUT.cardWidth / 2;
        const toY = child.y;
        const midY = (fromY + toY) / 2;

        const className =
          drop.basis === 'adoptive'
            ? styles.dropAdoptive
            : drop.basis === 'foster'
              ? styles.dropFoster
              : drop.basis === 'guardianship'
                ? styles.dropGuardianship
                : drop.crossBand
                  ? styles.dropCrossBand
                  : styles.drop;

        return (
          <g key={`${drop.id}-${drop.childId}`}>
            <title>{t(`enums.parent_basis.${drop.basis}`)}</title>
            <path
              className={className}
              d={`M ${String(fromX)} ${String(fromY)} L ${String(fromX)} ${String(midY)} L ${String(toX)} ${String(midY)} L ${String(toX)} ${String(toY)}`}
            />
            {(drop.basis === 'adoptive' || drop.basis === 'foster') && (
              // §4.5: a diamond at the child end, so the basis is readable even
              // where the dash pattern is hidden behind a card.
              <path
                className={className}
                d={`M ${String(toX)} ${String(toY - 10)} l 5 5 l -5 5 l -5 -5 z`}
                fill={drop.basis === 'adoptive' ? 'currentColor' : 'none'}
              />
            )}
          </g>
        );
      })}
    </g>
  );
}

function Brackets({ layout }: { layout: TreeLayout }): JSX.Element {
  return (
    <g>
      {layout.brackets.map((bracket) => {
        const a = layout.persons.get(bracket.a);
        const b = layout.persons.get(bracket.b);
        if (!a || !b) return null;
        const y = Math.min(a.y, b.y) - 10;
        return (
          <path
            key={bracket.id}
            className={styles.bracket}
            d={`M ${String(a.x + DEFAULT_LAYOUT.cardWidth / 2)} ${String(y)} L ${String(b.x + DEFAULT_LAYOUT.cardWidth / 2)} ${String(y)}`}
          />
        );
      })}
    </g>
  );
}

function Cards({
  layout,
  focus,
  selected,
  hidden,
  lod,
  onSelect,
  onPivot,
  onOpen,
  onInspect,
}: {
  layout: TreeLayout;
  focus: string;
  selected: string | null;
  hidden: TreeChartProps['hidden'];
  lod: string;
  onSelect: (id: string) => void;
  onPivot: (id: string) => void;
  onOpen: (id: string) => void;
  onInspect?: (id: string | null) => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <g>
      {[...layout.persons.values()].map((placed) => {
        const beyond = hidden[placed.id];
        const total = (beyond?.ancestors ?? 0) + (beyond?.descendants ?? 0);
        return (
          <g
            key={placed.id}
            id={`tree-card-${placed.id}`}
            className={styles.cardGroup}
            transform={`translate(${String(placed.x)},${String(placed.y)})`}
            tabIndex={selected === placed.id ? 0 : -1}
            role="button"
            aria-label={cardLabel(placed.person, t)}
            onClick={() => onSelect(placed.id)}
            onDoubleClick={() => onPivot(placed.id)}
            onPointerEnter={() => onInspect?.(placed.id)}
            onPointerLeave={() => onInspect?.(null)}
          >
            <rect
              className={
                placed.id === focus
                  ? styles.cardFocus
                  : placed.person.status === 'pending'
                    ? styles.cardPending
                    : styles.card
              }
              width={DEFAULT_LAYOUT.cardWidth}
              height={DEFAULT_LAYOUT.cardHeight}
              rx={8}
            />
            {lod !== 'low' && (
              // The name is the way out of the chart and into the person: click
              // the card to select it, click the name to go and read about them.
              <text
                className={styles.cardName}
                x={12}
                y={28}
                onClick={(event) => {
                  event.stopPropagation();
                  onOpen(placed.id);
                }}
              >
                {truncate(placed.person.display_label, 22)}
              </text>
            )}
            {lod === 'full' && (
              <text className={styles.cardDates} x={12} y={48}>
                {formatLifespan(placed.person.birth, placed.person.death) ?? ''}
              </text>
            )}
            {lod === 'full' && placed.person.status === 'pending' && (
              <text className={styles.cardDates} x={12} y={64}>
                {t('enums.assertion_status.pending')}
              </text>
            )}
            {total > 0 && lod !== 'low' && (
              // §4.7: the boundary of the expansion, and how much lies past it.
              <text
                className={styles.hiddenChip}
                x={DEFAULT_LAYOUT.cardWidth - 8}
                y={DEFAULT_LAYOUT.cardHeight - 8}
                textAnchor="end"
                onClick={(event) => {
                  event.stopPropagation();
                  onPivot(placed.id);
                }}
              >
                +{String(total)}
              </text>
            )}
            <title>{cardLabel(placed.person, t)}</title>
          </g>
        );
      })}
    </g>
  );
}

function cardLabel(person: PersonSummary, t: (key: string) => string): string {
  const span = formatLifespan(person.birth, person.death);
  const status = person.status === 'pending' ? ` (${t('enums.assertion_status.pending')})` : '';
  return `${person.display_label}${span ? `, ${span}` : ''}${status}`;
}

const truncate = (value: string, max: number): string =>
  value.length <= max ? value : `${value.slice(0, max - 1)}…`;
